/**
 * Language Detection Notice Component
 * ZO Bull Algo Trading Platform
 * 
 * Detects user's location via IP and suggests appropriate language
 * Version: 6.2.0
 */
import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { AVAILABLE_LANGUAGES } from '../i18n';
import './LanguageDetectionNotice.css';

// IP to Language mapping based on country/region
const IP_LANGUAGE_MAP: Record<string, { code: string; country: string }[]> = {
  // India - Multiple Indian languages
  'IN': [
    { code: 'hi', country: 'India' },
    { code: 'hi-en', country: 'India' },
    { code: 'mr', country: 'Maharashtra' },
    { code: 'ta', country: 'Tamil Nadu' },
    { code: 'te', country: 'Telangana/AP' },
    { code: 'kn', country: 'Karnataka' },
    { code: 'ml', country: 'Kerala' },
    { code: 'bn', country: 'West Bengal' },
    { code: 'gu', country: 'Gujarat' },
    { code: 'pa', country: 'Punjab' },
    { code: 'or', country: 'Odisha' },
    { code: 'as', country: 'Assam' },
    { code: 'bho', country: 'Bihar' },
    { code: 'mai', country: 'Bihar' },
    { code: 'ne', country: 'Nepal border' },
    { code: 'sa', country: 'Sanskrit lovers' },
  ],
  // Southeast Asia
  'TH': [{ code: 'th', country: 'Thailand' }],
  'VN': [{ code: 'vi', country: 'Vietnam' }],
  'ID': [{ code: 'id', country: 'Indonesia' }],
  'MY': [{ code: 'id', country: 'Malaysia' }],
  'PH': [{ code: 'en', country: 'Philippines' }],
  'SG': [{ code: 'en', country: 'Singapore' }],
  // East Asia
  'CN': [{ code: 'zh', country: 'China' }],
  'JP': [{ code: 'ja', country: 'Japan' }],
  'KR': [{ code: 'ko', country: 'South Korea' }],
  'TW': [{ code: 'zh', country: 'Taiwan' }],
  'HK': [{ code: 'zh', country: 'Hong Kong' }],
  // Middle East
  'SA': [{ code: 'ar', country: 'Saudi Arabia' }],
  'AE': [{ code: 'ar', country: 'UAE' }],
  'EG': [{ code: 'ar', country: 'Egypt' }],
  'IL': [{ code: 'he', country: 'Israel' }],
  'IR': [{ code: 'fa', country: 'Iran' }],
  'IQ': [{ code: 'ar', country: 'Iraq' }],
  'TR': [{ code: 'tr', country: 'Turkey' }],
  // Europe
  'DE': [{ code: 'de', country: 'Germany' }],
  'FR': [{ code: 'fr', country: 'France' }],
  'IT': [{ code: 'it', country: 'Italy' }],
  'ES': [{ code: 'es', country: 'Spain' }],
  'PT': [{ code: 'pt', country: 'Portugal' }],
  'NL': [{ code: 'nl', country: 'Netherlands' }],
  'PL': [{ code: 'pl', country: 'Poland' }],
  'UA': [{ code: 'uk', country: 'Ukraine' }],
  'RU': [{ code: 'ru', country: 'Russia' }],
  'GB': [{ code: 'en', country: 'UK' }],
  'IE': [{ code: 'en', country: 'Ireland' }],
  // Americas
  'US': [{ code: 'en', country: 'USA' }],
  'CA': [{ code: 'en', country: 'Canada' }],
  'MX': [{ code: 'es', country: 'Mexico' }],
  'BR': [{ code: 'pt', country: 'Brazil' }],
  'AR': [{ code: 'es', country: 'Argentina' }],
  'CO': [{ code: 'es', country: 'Colombia' }],
  'CL': [{ code: 'es', country: 'Chile' }],
  'PE': [{ code: 'es', country: 'Peru' }],
  // South Asia
  'PK': [{ code: 'ur', country: 'Pakistan' }],
  'BD': [{ code: 'bn', country: 'Bangladesh' }],
  'NP': [{ code: 'ne', country: 'Nepal' }],
  'LK': [{ code: 'ta', country: 'Sri Lanka' }],
  'AF': [{ code: 'fa', country: 'Afghanistan' }],
};

// Countries that commonly speak multiple languages
const MULTI_LANG_COUNTRIES = ['IN', 'PK', 'BD', 'LK', 'SG', 'MY', 'CA', 'CH', 'BE'];

// State/region to language mapping for India
const INDIA_REGION_MAP: Record<string, string> = {
  'MH': 'mr', // Maharashtra
  'TN': 'ta', // Tamil Nadu
  'KA': 'kn', // Karnataka
  'KL': 'ml', // Kerala
  'AP': 'te', // Andhra Pradesh
  'TS': 'te', // Telangana
  'WB': 'bn', // West Bengal
  'GJ': 'gu', // Gujarat
  'RJ': 'hi', // Rajasthan
  'PB': 'pa', // Punjab
  'HR': 'hi', // Haryana
  'DL': 'hi', // Delhi
  'UP': 'hi', // Uttar Pradesh
  'BR': 'bho', // Bihar
  'OR': 'or', // Odisha
  'AS': 'as', // Assam
  'MP': 'hi', // Madhya Pradesh
  'JK': 'ks', // Jammu & Kashmir
  'GA': 'kok', // Goa
};

interface Props {
  onDismiss?: () => void;
  onLanguageChange?: (langCode: string) => void;
}

const LanguageDetectionNotice: React.FC<Props> = ({ onDismiss, onLanguageChange }) => {
  const { t, i18n } = useTranslation();
  const [isVisible, setIsVisible] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [detectedLang, setDetectedLang] = useState<string | null>(null);
  const [detectedCountry, setDetectedCountry] = useState<string | null>(null);
  const [showLanguageList, setShowLanguageList] = useState(false);

  useEffect(() => {
    // Check if user has already dismissed this notice
    const dismissed = localStorage.getItem('langNoticeDismissed');
    const savedLang = localStorage.getItem('i18nextLng');
    
    // Don't show if user already chose a language or dismissed
    if (dismissed || (savedLang && savedLang !== 'en')) {
      setIsLoading(false);
      return;
    }

    // Detect user location via IP
    detectUserLocation();
  }, []);

  const detectUserLocation = async () => {
    try {
      // Using a free IP geolocation API
      const response = await fetch('https://ipapi.co/json/', {
        headers: { 'Accept': 'application/json' }
      });
      
      if (!response.ok) throw new Error('IP detection failed');
      
      const data = await response.json();
      
      const countryCode = data.country_code || data.country;
      const regionCode = data.region_code;
      
      if (!countryCode) throw new Error('No country detected');

      // Find recommended language based on country
      const langOptions = IP_LANGUAGE_MAP[countryCode];
      
      if (langOptions && langOptions.length > 0) {
        // For India, try to detect state/region
        let recommendedLang = langOptions[0].code;
        let countryName = langOptions[0].country;

        if (countryCode === 'IN' && regionCode && INDIA_REGION_MAP[regionCode]) {
          recommendedLang = INDIA_REGION_MAP[regionCode];
          countryName = 'Your region in India';
        }

        setDetectedLang(recommendedLang);
        setDetectedCountry(countryName);
        setIsVisible(true);
      } else {
        // Default to English for unmapped countries
        setDetectedLang('en');
        setDetectedCountry(data.country_name || 'Your location');
        setIsVisible(false);
      }
    } catch (error) {
      console.log('IP detection failed, using browser language');
      setIsLoading(false);
    }
  };

  const handleAcceptSuggestion = () => {
    if (detectedLang) {
      i18n.changeLanguage(detectedLang);
      localStorage.setItem('i18nextLng', detectedLang);
      onLanguageChange?.(detectedLang);
    }
    handleDismiss();
  };

  const handleDismiss = () => {
    setIsVisible(false);
    localStorage.setItem('langNoticeDismissed', 'true');
    onDismiss?.();
  };

  const handleShowAllLanguages = () => {
    setShowLanguageList(!showLanguageList);
  };

  const handleSelectLanguage = (langCode: string) => {
    i18n.changeLanguage(langCode);
    localStorage.setItem('i18nextLng', langCode);
    localStorage.setItem('langNoticeDismissed', 'true');
    onLanguageChange?.(langCode);
    setIsVisible(false);
    setShowLanguageList(false);
  };

  const getLanguageInfo = (code: string) => {
    return AVAILABLE_LANGUAGES.find(l => l.code === code) || { 
      code, 
      name: code.toUpperCase(), 
      nativeName: code, 
      flag: '🌐' 
    };
  };

  const suggestedLang = detectedLang ? getLanguageInfo(detectedLang) : null;

  if (isLoading || !isVisible) return null;

  return (
    <div className="language-detection-notice">
      <div className="lang-notice-backdrop" onClick={handleDismiss} />
      <div className="lang-notice-container">
        <button className="lang-notice-close" onClick={handleDismiss} aria-label="Close">
          ✕
        </button>

        <div className="lang-notice-header">
          <span className="lang-notice-icon">🌍</span>
          <h3>{t('Switch to your language') || 'Switch to your language'}</h3>
        </div>

        <p className="lang-notice-message">
          {t('We detected you are from', { country: detectedCountry || 'your location' })} {' '}
          {!showLanguageList ? (
            <>
              <strong>{suggestedLang?.nativeName || detectedLang?.toUpperCase()}</strong>
              {' '}{t('is available!')}
            </>
          ) : (
            <>Select your preferred language:</>
          )}
        </p>

        {!showLanguageList ? (
          <>
            <div className="lang-notice-suggestion">
              <div className="suggested-lang-card">
                <span className="lang-flag">{suggestedLang?.flag}</span>
                <div className="lang-info">
                  <span className="lang-native">{suggestedLang?.nativeName}</span>
                  <span className="lang-name">{suggestedLang?.name}</span>
                </div>
              </div>
            </div>

            <div className="lang-notice-actions">
              <button 
                className="btn btn-primary btn-sm"
                onClick={handleAcceptSuggestion}
              >
                <span>✓</span> {t('Yes, switch to')} {suggestedLang?.name}
              </button>
              <button 
                className="btn btn-secondary btn-sm"
                onClick={handleShowAllLanguages}
              >
                {t('Choose another language')} →
              </button>
            </div>
          </>
        ) : (
          <div className="lang-notice-language-grid">
            {/* Indian Languages */}
            <div className="lang-category">
              <h4>🇮🇳 Indian Languages</h4>
              <div className="lang-grid">
                {AVAILABLE_LANGUAGES.filter(l => 
                  ['hi', 'hi-en', 'mr', 'ta', 'te', 'kn', 'ml', 'bn', 'gu', 'pa', 'or', 'as', 'bho', 'ne', 'sa', 'ur', 'ks', 'kok'].includes(l.code)
                ).map(lang => (
                  <button
                    key={lang.code}
                    className={`lang-option ${i18n.language === lang.code ? 'active' : ''}`}
                    onClick={() => handleSelectLanguage(lang.code)}
                  >
                    <span className="lang-flag">{lang.flag}</span>
                    <span className="lang-native">{lang.nativeName}</span>
                  </button>
                ))}
              </div>
            </div>

            {/* International Languages */}
            <div className="lang-category">
              <h4>🌍 International</h4>
              <div className="lang-grid">
                {AVAILABLE_LANGUAGES.filter(l => 
                  ['en', 'es', 'pt', 'fr', 'de', 'it', 'ru', 'zh', 'ja', 'ko', 'ar', 'fa', 'tr', 'th', 'vi', 'id', 'pl', 'uk', 'nl'].includes(l.code)
                ).map(lang => (
                  <button
                    key={lang.code}
                    className={`lang-option ${i18n.language === lang.code ? 'active' : ''}`}
                    onClick={() => handleSelectLanguage(lang.code)}
                  >
                    <span className="lang-flag">{lang.flag}</span>
                    <span className="lang-native">{lang.nativeName}</span>
                  </button>
                ))}
              </div>
            </div>
          </div>
        )}

        <div className="lang-notice-footer">
          <button className="lang-dismiss-link" onClick={handleDismiss}>
            {t('No thanks, keep English')} →
          </button>
        </div>
      </div>
    </div>
  );
};

export default LanguageDetectionNotice;
